import * as z from "zod";
import { EntityContext } from "@archipad-js/core/orm";
import { PatchId } from "./patch.types";
import { MutablePatch } from "./mutablePatch";

export const PatchHandlerContext = {
    UNKNOWN: "UNKNOWN",
    SYNCHRONIZE: "SYNCHRONIZE",
    TEMP: "TEMP",
    UPGRADE: "UPGRADE",
    DUMP_PATCH: "DUMP_PATCH",
    PROJECT_SYNC: "PROJECT_SYNC",
} as const;

const PatchHandlerContextEnum = z.nativeEnum(PatchHandlerContext);
export type PatchHandlerContext = z.infer<typeof PatchHandlerContextEnum>;

/**
 * Each patch handler module must export a 'handler' property that implements the
 * following interface.
 */
export interface PatchHandler {
    /** called before applying a list of patches on the orm */
    willImportPatches?(
        entityContext: EntityContext,
        projectId: string,
        patchHandlerContext: PatchHandlerContext,
    ): Promise<void>;
    /** called after applying a list of patches on the orm */
    endImportPatches?(
        entityContext: EntityContext,
        projectId: string,
        patchHandlerContext: PatchHandlerContext,
    ): Promise<void>;
    /** called before creating a patch */
    willCreatePatch?(
        entityContext: EntityContext,
        projectId: string,
        patchHandlerContext: PatchHandlerContext,
    ): Promise<void>;

    /** called before exporting a patch */
    willExportPatch?(
        entityContext: EntityContext,
        projectId: string,
        patchHandlerContext: PatchHandlerContext,
        patch: MutablePatch,
        patchId?: PatchId,
    ): Promise<void>;
    /** called before applying a patch on the orm */
    willImportPatch?(
        entityContext: EntityContext,
        projectId: string,
        patchHandlerContext: PatchHandlerContext,
        patch: MutablePatch,
        patchId?: PatchId,
    ): Promise<void>;
    /** called after applying the patch on the orm */
    didImportPatch?(
        entityContext: EntityContext,
        projectId: string,
        patchHandlerContext: PatchHandlerContext,
        patch: MutablePatch,
        patchId?: PatchId,
    ): Promise<void>;
}

type PatchHandlerEventType = keyof PatchHandler;

export type PatchHandlerEventRelativeToPatch = Extract<
    PatchHandlerEventType,
    "willImportPatch" | "didImportPatch" | "willExportPatch"
>;
function isPatchHandlerEventRelativeToPatch(event: PatchHandlerEventType): event is PatchHandlerEventRelativeToPatch {
    return event === "willImportPatch" || event === "didImportPatch" || event === "willExportPatch";
}

export type PatchHandlerEvent = Extract<
    PatchHandlerEventType,
    "willImportPatches" | "endImportPatches" | "willCreatePatch"
>;
function isPatchHandlerEvent(event: PatchHandlerEventType): event is PatchHandlerEvent {
    return event === "willImportPatches" || event === "endImportPatches" || event === "willCreatePatch";
}

export class PatchHandlerService {
    constructor() {
        this.patchHandlers = [];
    }

    /** patch handlers */
    readonly patchHandlers: PatchHandler[];

    /**
     * Dispatch the given event on the patch handlers.
     * PatchHandlerEventRelativeToPatch --> This event is applied on each patch.
     * PatchHandlerEventRelativeToPatches --> This event is applied once per list of patches.
     *
     * The patch handlers are dynamically loaded modules which can alter patches
     * before or after they are imported or exported from the orm.
     *
     * @param event the event to dispatch
     * @param entityContext the current entity context
     * @param projectId the root entity entity in the context
     * @param patch the patch which is the object of the event
     */
    executePatchHandlers(
        event: PatchHandlerEvent,
        entityContext: EntityContext,
        projectId: string,
        patchHandlerContext: PatchHandlerContext,
    ): Promise<void>;
    executePatchHandlers(
        event: PatchHandlerEventRelativeToPatch,
        entityContext: EntityContext,
        projectId: string,
        patchHandlerContext: PatchHandlerContext,
        patch: MutablePatch,
        patchId?: PatchId,
    ): Promise<void>;
    async executePatchHandlers(
        event: PatchHandlerEventType,
        entityContext: EntityContext,
        projectId: string,
        patchHandlerContext: PatchHandlerContext,
        patch?: MutablePatch,
        patchId?: PatchId,
    ): Promise<void> {
        // dispatch the event in turn to each handler that implements the method
        for (const patchHandler of this.patchHandlers) {
            if (!patchHandler[event]) {
                continue;
            }
            if (isPatchHandlerEventRelativeToPatch(event)) {
                const fn = patchHandler[event].bind(patchHandler);
                await fn(entityContext, projectId, patchHandlerContext, patch, patchId);
            } else if (isPatchHandlerEvent(event)) {
                const fn = patchHandler[event].bind(patchHandler);
                await fn(entityContext, projectId, patchHandlerContext);
            }
        }
    }

    registerPatchHandler(patchHandler: PatchHandler) {
        this.patchHandlers.push(patchHandler);
    }
}
